import Loading from "../../Generic/loading";
import { parseEmsDate } from "../../Logic/emsDates";
import { MailboxHealthCard } from "../MailboxAPI";
import { backlogCoverage } from "../mailboxHealth";
import { InspectionState, useTenantInspection } from "../useTenantInspection";
import MailboxList from "./MailboxList";

/**
 * Deep-link target for the tenant email worklist: a tenant, optionally
 * narrowed to one of its mailboxes.
 *
 * Defined here, not in `TenantEmails` (its eventual consumer), because the
 * existing import direction runs the other way: `TenantEmails/index.tsx`
 * already imports from `CoreMailProcessing/MailboxAPI.ts`. Sourcing this type
 * from `TenantEmails` instead would add a second, reverse edge between the
 * same two folders where none exists today. Defined here — where the only
 * value of this shape is actually constructed, in the "View failures" handler
 * below — `CoreMailProcessing/index.tsx` picks it up the same way it already
 * picks up this component (one direction, already established), and
 * re-exports it for `Main.tsx`, which already imports that module.
 */
export interface EmailTarget {
    tenantKey: string;
    mailboxID?: number;
}

interface iTenantDetailProps {
    tenantKey: string;
    /**
     * From the triage row that was clicked.
     *
     * A prop rather than something read from the response, so the header
     * renders immediately — before the token is minted and before any mailbox
     * data exists. The view is never headerless.
     */
    name: string;
    onBack: () => void;
    /**
     * Opens the email worklist for this tenant.
     *
     * OPTIONAL, deliberately. The worklist's own tenant picker is the primary
     * entry; this is a convenience from the drill-down. A caller that does not
     * supply it simply gets no control, rather than one that does nothing.
     */
    onOpenEmails?: (target: EmailTarget) => void;
}

/**
 * The freshest reading in the response.
 *
 * There is no response envelope on this route — it returns a bare array — so
 * there is no response-level `asOf` and each card carries its own. They all
 * come from one request and should agree; taking the maximum means a
 * disagreement reads as the freshest reading rather than an arbitrary one.
 *
 * `asOf` is stamped per request and DOES carry an offset, unlike the
 * database-sourced run timestamps (contract §1.3). It still goes through
 * `parseEmsDate`, which passes an offset-bearing value straight through.
 */
function latestRead(mailboxes: MailboxHealthCard[]): Date | undefined {
    const stamps = mailboxes
        .map(mailbox => parseEmsDate(mailbox.asOf))
        .filter((stamp): stamp is Date => stamp !== undefined);
    return stamps.length === 0 ? undefined : new Date(Math.max(...stamps.map(stamp => stamp.getTime())));
}

/**
 * The header's backlog-coverage sentence, or nothing.
 *
 * Says something only when it has something to say: a partial sum. Full
 * coverage needs no caveat, and a tenant with no receiving mailboxes has no
 * coverage question at all. Silence when everything is measured is what keeps
 * the sentence meaningful when it does appear.
 *
 * The zero case needs no clause of its own. With no receiving mailboxes,
 * `measured` and `receiving` are both `0`, so the equality below already
 * answers "complete" — see `backlogCoverage`, which documents that as
 * deliberate. An explicit `receiving === 0` guard here would read as
 * load-bearing while being unable to change the outcome, which is worse than
 * no guard: the next reader would have to prove it dead before touching it.
 */
function coverageNote(mailboxes: MailboxHealthCard[]): string | undefined {
    const coverage = backlogCoverage(mailboxes);
    if (coverage.measured === coverage.receiving) {
        return undefined;
    }
    return (
        `Backlog measured on ${coverage.measured} of ${coverage.receiving} receiving mailboxes — ` +
        `this tenant's backlog total covers only those.`
    );
}

/**
 * Copy for the states that render an explanation rather than content.
 *
 * A `Record`, so a seventh `InspectionState` cannot compile until it has copy.
 * Four sibling `state === "x" &&` conditionals would instead have rendered a
 * header over nothing — indistinguishable from a tenant mid-load, with every
 * test still green. Same argument `mailboxHealth.ts` makes for its STANDING
 * table, and the same guard MailboxRow's own lookup tables carry.
 *
 * `loading` and `ready` stay explicit branches below: they render components,
 * not prose. Keeping the four notices adjacent also puts the notEntitled and
 * forbidden wording side by side, which is where their difference has to be
 * legible.
 */
const NOTICE: Record<Exclude<InspectionState, "loading" | "ready">, string> = {
    // NOT an error panel, and no Retry. This is the expected outcome of
    // making every triage row clickable (spec §2.3): a sys-admin can read
    // every tenant through the Core routes without being entitled to
    // elevate into each one. Retrying an entitlement you do not have cannot
    // succeed, and alarm styling here would train operators to read a
    // permissions boundary as a broken page.
    notEntitled:
        "You do not have access to this tenant. The cross-tenant overview reads every tenant, but opening " +
        "one needs access to that tenant specifically — ask for it if you need to inspect this one.",
    // A different sentence from notEntitled on purpose: the elevation
    // SUCCEEDED here and the tenant's own API still refused. That indicts a
    // different thing, and collapsing the two would destroy the only
    // information that says which.
    forbidden:
        "This tenant's mail-processing endpoints refused the request. Opening the tenant worked, so the block " +
        "is inside that tenant rather than on your access to it.",
    unauthorised: "Your session has expired. Reload the page to sign in again.",
    failed: "Reading this tenant's mailboxes failed.",
};

function TenantDetail({ tenantKey, name, onBack, onOpenEmails }: iTenantDetailProps) {
    const { state, mailboxes, refreshing, refresh } = useTenantInspection(tenantKey);

    const read = latestRead(mailboxes);
    const readClock = read?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const note = state === "ready" ? coverageNote(mailboxes) : undefined;

    async function handleRefresh() {
        await refresh();
    }

    return (
        /*
            A real element rather than a fragment, so this page has something to
            carry its own padding. As a fragment its children were direct children
            of `.bodyContainer`, which has none — so the crumb, the header and the
            mailbox table all ran hard against the left nav and the right window
            edge. Matches the email detail's inset.
        */
        <div className="mp-tenant-detail">
            <div className="mp-crumb">
                <button
                    type="button"
                    className="mp-crumb-btn"
                    onClick={onBack}
                >
                    ← All tenants
                </button>
            </div>

            <div className="topSection">
                <div className="sectionHeader">{name}</div>
                {/*
                    The button is wrapped, and must STAY wrapped.

                    `.topSection :nth-child(2)` in Main/index.css gives the
                    header's second child `flex: 0 0 300px`, its own padding and
                    `text-align: right`. That rule is written for a CONTAINER of
                    controls, which is what every other page puts there — see
                    Mailboxes/memberships.tsx. An unwrapped button does not sit
                    inside that box, it BECOMES it: 300 x 47px, stretched to the
                    header's full height, with the label shoved into the corner.

                    The div takes the slot; the buttons keep their natural
                    size.

                    But the selector is a DESCENDANT one, not a child one, so it
                    reaches inside this div as well as matching it. With one
                    button that was inert — it matched `:nth-child(1)`. With
                    two, the second matches `:nth-child(2)` and takes the slot's
                    own 8px top padding a second time, so Refresh rendered 4px
                    taller than the button beside it. `mp-detail-controls`
                    undoes that.
                */}
                {state === "ready" && (
                    <div className="mp-detail-controls">
                        {onOpenEmails && (
                            <button
                                className="g-btn btn-default"
                                onClick={() => onOpenEmails({ tenantKey })}
                            >
                                View failures
                            </button>
                        )}
                        <button
                            className="g-btn btn-default"
                            onClick={() => void handleRefresh()}
                            disabled={refreshing}
                        >
                            {refreshing ? "Refreshing…" : "Refresh"}
                        </button>
                    </div>
                )}
            </div>

            <div className="mp-detail-meta">
                <span className="mp-detail-key">{tenantKey}</span>
                {state === "ready" && (
                    <>
                        <span>
                            {mailboxes.length} {mailboxes.length === 1 ? "mailbox" : "mailboxes"}
                        </span>
                        {readClock && <span>read {readClock}</span>}
                    </>
                )}
            </div>

            {/*
                The coverage sentence lives here, in the header block, rather
                than beside any one mailbox: it is a statement about the whole
                list. It also explains why this tenant's figures may disagree
                with its triage row — the other reason being that the triage
                row is a snapshot up to five minutes old and these are live
                tables. The two are not meant to reconcile.
            */}
            {note && <div className="mp-detail-coverage">{note}</div>}

            {state === "loading" && <Loading></Loading>}

            {state !== "loading" && state !== "ready" && (
                <div className="mp-notice">
                    <p>{NOTICE[state]}</p>
                    {state === "failed" && (
                        <button
                            className="g-btn btn-default"
                            onClick={() => void handleRefresh()}
                            disabled={refreshing}
                        >
                            {refreshing ? "Retrying…" : "Retry"}
                        </button>
                    )}
                </div>
            )}

            {state === "ready" && <MailboxList mailboxes={mailboxes} />}
        </div>
    );
}

export default TenantDetail;
